import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiUpload, HiDownload, HiCheck, HiHeart, HiX, HiPlus, HiTrash } from 'react-icons/hi';
import Papa from 'papaparse';
import api from '../utils/api';
import toast from 'react-hot-toast';
import SearchableDropdown from './common/SearchableDropdown';
import { AppPageShell, PageHeader } from './common/ui';
import ReactDOM from 'react-dom';

const SavePromptModal = ({ isOpen, onClose, onSave, initialName = '', initialPrompt = '', isEditing = false }) => {
  const [promptName, setPromptName] = useState(initialName);
  const [promptText, setPromptText] = useState(initialPrompt);

  useEffect(() => {
    setPromptName(initialName);
    setPromptText(initialPrompt);
  }, [initialName, initialPrompt]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {isEditing ? 'Edit Prompt' : 'Save Prompt'}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt Name
            </label>
            <input
              type="text"
              value={promptName}
              onChange={(e) => setPromptName(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Enter a name for your prompt"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt Text
            </label>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              className="w-full p-2 border rounded"
              rows="4"
              placeholder="Enter your prompt text"
            />
          </div>
        </div>
        <div className="mt-6 flex flex-row gap-3">
          <button
            onClick={() => onSave(promptName, promptText)}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            disabled={!promptName.trim() || !promptText.trim()}
          >
            {isEditing ? 'Update' : 'Save'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const SearchablePromptDropdown = ({ prompts, onSelect, onEdit, onDelete, selectedPromptId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredPrompts = prompts.filter(prompt =>
    prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prompt.prompt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-2.5 text-base border border-gray-300 rounded-md bg-white hover:bg-gray-50 font-medium ${selectedPromptId ? 'bg-blue-50' : ''}`}
      >
        <span>Load Saved Prompt</span>
        <svg
          className={`w-5 h-5 ml-2 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-[400px] right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2.5 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {filteredPrompts.length === 0 ? (
              <div className="px-4 py-3 text-base text-gray-500">
                No prompts found
              </div>
            ) : (
              <div className="py-2">
                {filteredPrompts.map((prompt) => (
                  <div
                    key={prompt.id}
                    className="group px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex justify-between items-start">
                      <div
                        className="flex-1 pr-4"
                        onClick={() => {
                          onSelect(prompt.id);
                          setIsOpen(false);
                          setSearchTerm('');
                        }}
                      >
                        <div className="text-base font-medium text-gray-900 mb-1">{prompt.name}</div>
                        <div className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                          {prompt.prompt}
                        </div>
                      </div>
                      <div className="flex space-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(prompt.id);
                          }}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(prompt.id);
                          }}
                          className="text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const STEPS = [
  { id: 'recipients', name: 'Recipients', description: 'Add recipients to campaign', required: true },
  { id: 'configuration', name: 'Configuration', description: 'Configure campaign settings', required: true }
];

const CAMPAIGN_BATCH_RETRY_DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

/** Batch campaigns only support a single no-answer redial sequence (server maps this to the provider batch API). */
function validateCampaignBatchRetrySteps(retrySteps) {
  if (!retrySteps || retrySteps.length === 0) return null;
  if (retrySteps.length > 1) {
    return 'Only one retry step is allowed. Remove the extra retry & follow-up steps.';
  }
  const step = retrySteps[0];
  const conds = step.retryConditions || {};
  if (Object.values(conds).some(Boolean)) {
    return 'Retries only run when there is no answer. Uncheck all options under Retry Conditions, or remove retry steps.';
  }
  const fu = step.followUp || {};
  if (fu.dialogFlow_id) {
    return 'A different pathway on follow-up is not supported. Clear Follow Up, or remove retry steps.';
  }
  if (fu.prompt && String(fu.prompt).trim()) {
    return 'A different follow-up prompt is not supported. Clear Follow Up, or remove retry steps.';
  }
  const days = step.allowedDays || {};
  const allOn = CAMPAIGN_BATCH_RETRY_DAY_KEYS.every((k) => days[k]);
  if (!allOn) {
    return 'All days of the week must be enabled for retries, or remove retry steps.';
  }
  return null;
}

function CampaignStepRailDot({ status }) {
  if (status === 'current') {
    return (
      <span className="relative flex h-7 items-center justify-center" aria-hidden>
        <span className="relative flex h-4 w-4 items-center justify-center">
          <span
            className="absolute h-7 w-7 rounded-full opacity-35 blur-md"
            style={{ background: 'var(--app-accent)' }}
          />
          <span className="relative z-10 inline-flex h-3.5 w-3.5 rounded-full border-2 border-[var(--app-accent)] bg-[var(--app-accent)] shadow-[0_0_0_5px_var(--app-accent-soft)]" />
        </span>
      </span>
    );
  }
  if (status === 'complete') {
    return (
      <span className="relative flex h-7 items-center justify-center" aria-hidden>
        <span className="relative z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--app-accent)] text-white shadow-sm ring-4 ring-[var(--app-surface-strong)]">
          <HiCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
      </span>
    );
  }
  return (
    <span className="relative flex h-7 items-center justify-center" aria-hidden>
      <span className="relative z-10 inline-flex h-3.5 w-3.5 rounded-full border-2 border-[var(--app-muted)] bg-[var(--app-surface-strong)] opacity-90 ring-4 ring-[var(--app-surface-strong)]" />
    </span>
  );
}

const CreateCampaignPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('recipients');
  const [dialogFlows, setDialogFlows] = useState([]);
  const [csvData, setCsvData] = useState([{
    phone_number: '',
    name: '',
    company: '',
    product: ''
  }]);
  const fileInputRef = useRef(null);
  const [retrySteps, setRetrySteps] = useState([]);
  const [formData, setFormData] = useState({
    name: 'Untitled Campaign',
    model: 'base',
    mode: 'prompt',
    prompt: '',
    dialogFlow_id: '',
    language: 'en',
    voice: 'nat',
    temperature: null,
    background_track: 'none',
    record: false,
    wait_for_greeting: false,
    noise_cancellation: false,
    local_dialing: false,
    answered_by_enabled: false,
    voicemail_message: '',
    retrySteps: []  // Array to store multiple retry & follow up configurations
  });

  // Add new state variables for prompt management
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [showSavePromptModal, setShowSavePromptModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // Add new state variables for voice selection
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voiceCategory, setVoiceCategory] = useState('curated');

  const filterCampaignVoiceOptions = useCallback(
    (options, { inputValue }) => {
      const q = (inputValue || '').toLowerCase().trim();
      const matchesSearch = (voice) => {
        if (!q) return true;
        return (
          (voice.name || '').toLowerCase().includes(q) ||
          (voice.description || '').toLowerCase().includes(q) ||
          (voice.tags || []).some((tag) => (tag || '').toLowerCase().includes(q))
        );
      };

      return options.filter((voice) => {
        if (!matchesSearch(voice)) return false;
        switch (voiceCategory) {
          case 'curated':
            return !voice.is_user_voice && voice.public;
          case 'your':
            return voice.is_user_voice;
          case 'favorites':
            return voice.is_favorite;
          case 'all':
            return voice.public;
          default:
            return false;
        }
      });
    },
    [voiceCategory]
  );

  const [activeSteps, setActiveSteps] = useState(STEPS.filter(step => step.required));

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch dialog flows
        const flowsResponse = await api.getdialogFlows();
        let pathways = [];
        if (Array.isArray(flowsResponse)) {
          pathways = flowsResponse;
        } else if (flowsResponse.pathways) {
          pathways = flowsResponse.pathways;
        } else if (flowsResponse.data && flowsResponse.data.pathways) {
          pathways = flowsResponse.data.pathways;
        }
        
        const mappedFlows = pathways.map(pathway => ({
          id: pathway.id,
          name: pathway.name || `Pathway ${pathway.id}`
        }));
        setDialogFlows(mappedFlows);

        // Fetch voices
        const voicesResponse = await api.getVoices();
        if (voicesResponse.voices) {
          setVoices(voicesResponse.voices);
        }

        // Fetch saved prompts
        const promptsResponse = await api.getPrompts();
        if (promptsResponse.prompts) {
          setSavedPrompts(promptsResponse.prompts);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  const handleDownloadStarterCSV = () => {
    const csvContent = "phone_number\n+14155555555";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'starter_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadComplexCSV = () => {
    const csvContent = "phone_number,name,company,product\n+14155555555,John Doe,Acme Inc,Premium Plan";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'complex_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          if (!results.data[0]?.phone_number) {
            toast.error('CSV must have a "phone_number" column');
            return;
          }
          setCsvData(results.data.filter(row => Object.values(row).some(value => value)));
          toast.success('CSV file uploaded successfully');
        },
        error: (error) => {
          toast.error('Error parsing CSV file');
          console.error('CSV Parse Error:', error);
        }
      });
    }
  };

  const handleRemoveCsvColumn = (columnKey) => {
    const keys = Object.keys(csvData[0] || {});
    if (keys.length <= 1) {
      toast.error('At least one column is required.');
      return;
    }
    if (String(columnKey).toLowerCase() === 'phone_number') {
      toast.error('The phone_number column is required and cannot be removed.');
      return;
    }
    setCsvData(
      csvData.map((row) => {
        const next = { ...row };
        delete next[columnKey];
        return next;
      })
    );
    toast.success(`Removed column “${columnKey}”`);
  };

  const handleSubmit = async () => {
    try {
      const campaignRetryError = validateCampaignBatchRetrySteps(formData.retrySteps);
      if (campaignRetryError) {
        toast.error(campaignRetryError);
        return;
      }

      // Start with the common fields
      const requestData = {
        base_prompt: formData.prompt || '', // Always include base_prompt, empty string if using dialog flow
        call_data: csvData,
        label: formData.name,
        test_mode: false,
        model: formData.model,
        language: formData.language,
        voice: formData.voice,
        temperature: formData.temperature || undefined,
        record: formData.record,
        wait_for_greeting: formData.wait_for_greeting,
        noise_cancellation: formData.noise_cancellation,
        local_dialing: formData.local_dialing,
        answered_by_enabled: formData.answered_by_enabled
      };

      // Add pathway_id if using a dialog flow
      if (formData.dialogFlow_id) {
        requestData.pathway_id = formData.dialogFlow_id;
      }

      // Add background track only if it's not 'none'
      if (formData.background_track !== 'none') {
        requestData.background_track = formData.background_track;
      }

      // Add voicemail message only if it's not empty
      if (formData.voicemail_message) {
        requestData.voicemail_message = formData.voicemail_message;
      }

      // Add retry configurations if any exist
      if (formData.retrySteps.length > 0) {
        requestData.retry_config = formData.retrySteps.map(step => ({
          conditions: Object.entries(step.retryConditions)
            .filter(([_, value]) => value)
            .map(([key]) => key),
          max_attempts: step.maxRetryAttempts,
          delay_hours: step.retryTiming.days * 24 + step.retryTiming.hours,
          allowed_days: Object.entries(step.allowedDays)
            .filter(([_, value]) => value)
            .map(([day]) => day.toLowerCase()),
          time_window: {
            start: step.timeWindow.start,
            end: step.timeWindow.end,
            timezone: step.timezone
          },
          follow_up: step.followUp.dialogFlow_id ? {
            pathway_id: step.followUp.dialogFlow_id
          } : {
            prompt: step.followUp.prompt,
            model: step.followUp.model,
            voice: step.followUp.voice,
            temperature: step.followUp.temperature
          }
        }));
      }

      // Remove any undefined or null values from the request
      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined || requestData[key] === null) {
          delete requestData[key];
        }
      });

      console.log('Sending request with payload:', requestData);
      const response = await api.createBatch(requestData);

      if (response.data?.batch_id && !response.errors) {
        toast.success('Campaign created successfully');
        navigate('/app/campaigns');
      } else {
        throw new Error(response.message || 'Failed to create campaign');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create campaign');
      console.error('Error creating campaign:', error);
    }
  };

  // Add prompt management handlers
  const handleSavePrompt = async (name, promptText) => {
    const loadingToast = toast.loading(editingPrompt ? 'Updating prompt...' : 'Saving prompt...');
    try {
      if (editingPrompt) {
        const response = await api.updatePrompt(editingPrompt.id, {
          prompt: promptText,
          name: name
        });

        if (response.status === 'success') {
          const promptsResponse = await api.getPrompts();
          if (promptsResponse.prompts) {
            setSavedPrompts(promptsResponse.prompts);
          }
          setShowSavePromptModal(false);
          setEditingPrompt(null);
          toast.success('Prompt updated successfully!', { id: loadingToast });
        }
      } else {
        const response = await api.createPrompt({
          prompt: promptText,
          name: name
        });

        if (response.status === 'success') {
          const promptsResponse = await api.getPrompts();
          if (promptsResponse.prompts) {
            setSavedPrompts(promptsResponse.prompts);
            
            const newPrompt = promptsResponse.prompts.find(p => p.name === name && p.prompt === promptText);
            if (newPrompt) {
              setSelectedPrompt(newPrompt);
              setFormData(prev => ({
                ...prev,
                prompt: newPrompt.prompt,
                dialogFlow_id: ''
              }));
            }
          }
          setShowSavePromptModal(false);
          toast.success('Prompt saved successfully!', { id: loadingToast });
        }
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error(`Failed to ${editingPrompt ? 'update' : 'save'} prompt: ${error.message}`, { id: loadingToast });
    }
  };

  const handlePromptSelect = async (promptId) => {
    const loadingToast = toast.loading('Loading prompt...');
    try {
      const response = await api.getPromptDetails(promptId);
      if (response.status === 'success' && response.prompt) {
        setSelectedPrompt(response.prompt);
        setFormData(prev => ({
          ...prev,
          prompt: response.prompt.prompt,
          dialogFlow_id: ''
        }));
        toast.success('Prompt loaded successfully', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error loading prompt:', error);
      toast.error(`Failed to load prompt: ${error.message}`, { id: loadingToast });
    }
  };

  const handleEditPrompt = async (promptId) => {
    const loadingToast = toast.loading('Loading prompt for edit...');
    try {
      const response = await api.getPromptDetails(promptId);
      if (response.status === 'success' && response.prompt) {
        setEditingPrompt(response.prompt);
        setShowSavePromptModal(true);
        toast.success('Prompt loaded for editing', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error loading prompt for edit:', error);
      toast.error(`Failed to load prompt: ${error.message}`, { id: loadingToast });
    }
  };

  const handleDeletePrompt = async (promptId) => {
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      const loadingToast = toast.loading('Deleting prompt...');
      try {
        const response = await api.deletePrompt(promptId);
        if (response.status === 'success') {
          const promptsResponse = await api.getPrompts();
          if (promptsResponse.prompts) {
            setSavedPrompts(promptsResponse.prompts);
          }
          toast.success('Prompt deleted successfully!', { id: loadingToast });
        }
      } catch (error) {
        console.error('Error deleting prompt:', error);
        toast.error(`Failed to delete prompt: ${error.message}`, { id: loadingToast });
      }
    }
  };

  const handleDirectPromptUpdate = async () => {
    if (!selectedPrompt) return;
    
    const loadingToast = toast.loading('Updating prompt...');
    try {
      const response = await api.updatePrompt(selectedPrompt.id, {
        prompt: formData.prompt,
        name: selectedPrompt.name
      });

      if (response.status === 'success') {
        const promptsResponse = await api.getPrompts();
        if (promptsResponse.prompts) {
          setSavedPrompts(promptsResponse.prompts);
        }
        toast.success('Prompt updated successfully!', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      toast.error(`Failed to update prompt: ${error.message}`, { id: loadingToast });
    }
  };

  const clearSelectedPrompt = () => {
    setSelectedPrompt(null);
    setFormData(prev => ({ ...prev, prompt: '' }));
  };

  const handleAddStep = () => {
    const newStepId = `retry-config-${retrySteps.length + 1}`;
    const newStep = {
      id: newStepId,
      name: `Retry & Follow Up ${retrySteps.length + 1}`,
      description: 'Set retry conditions and follow-up actions',
      required: false
    };
    
    setRetrySteps(prev => [...prev, newStep]);
    setFormData(prev => ({
      ...prev,
      retrySteps: [...prev.retrySteps, {
        id: newStepId,
        activeRetrySection: 'retry',
        retryConditions: {
          notInterested: false,
          interested: false,
          followUpRequired: false
        },
        maxRetryAttempts: 1,
        retryTiming: {
          days: 0,
          hours: 0
        },
        allowedDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true
        },
        timeWindow: {
          start: '09:00',
          end: '20:00'
        },
        timezone: 'America/Los_Angeles',
        followUp: {
          dialogFlow_id: '',
          prompt: '',
          model: formData.model,
          voice: 'nat',
          temperature: null
        }
      }]
    }));
  };

  const handleRemoveStep = (stepId) => {
    setRetrySteps(prev => prev.filter(step => step.id !== stepId));
    setFormData(prev => ({
      ...prev,
      retrySteps: prev.retrySteps.filter(step => step.id !== stepId)
    }));
    if (currentStep === stepId) {
      setCurrentStep('configuration');
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepChange = (stepId) => {
    setCurrentStep(stepId);
    scrollToTop();
  };

  const renderStepContent = () => {
    // If the current step is a retry step
    if (currentStep.startsWith('retry-config-')) {
      const stepIndex = retrySteps.findIndex(step => step.id === currentStep);
      if (stepIndex === -1) return null;

      const retryStep = formData.retrySteps[stepIndex];
      
      return (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            {/* Section Toggle */}
            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    retrySteps: prev.retrySteps.map((step, idx) => 
                      idx === stepIndex 
                        ? { ...step, activeRetrySection: 'retry' }
                        : step
                    )
                  }));
                }}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  retryStep.activeRetrySection === 'retry'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Retry Configuration
              </button>
              <button
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    retrySteps: prev.retrySteps.map((step, idx) => 
                      idx === stepIndex 
                        ? { ...step, activeRetrySection: 'followup' }
                        : step
                    )
                  }));
                }}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  retryStep.activeRetrySection === 'followup'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Follow Up
              </button>
            </div>

            {retryStep.activeRetrySection === 'retry' ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Retry Conditions</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Select outcomes from the previous call that should trigger a retry
                  </p>
                  <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <strong className="font-medium">Campaign batches:</strong> only simple redials are supported (same task/pathway, retry when there is no answer). Leave all outcome boxes unchecked, use a single retry step, leave Follow Up empty, and enable all days of the week. Outcome-based retries or a different follow-up prompt/pathway are not supported.
                  </div>
                  <div className="space-y-2">
                    {Object.entries({
                      notInterested: 'Not Interested',
                      interested: 'Interested',
                      followUpRequired: 'Follow Up Required'
                    }).map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={retryStep.retryConditions[key]}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              retrySteps: prev.retrySteps.map((step, idx) => 
                                idx === stepIndex 
                                  ? {
                                      ...step,
                                      retryConditions: {
                                        ...step.retryConditions,
                                        [key]: e.target.checked
                                      }
                                    }
                                  : step
                              )
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Maximum Retry Attempts</h3>
                  <input
                    type="number"
                    value={retryStep.maxRetryAttempts}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        retrySteps: prev.retrySteps.map((step, idx) => 
                          idx === stepIndex 
                            ? {
                                ...step,
                                maxRetryAttempts: Math.max(1, parseInt(e.target.value) || 1)
                              }
                            : step
                        )
                      }));
                    }}
                    min="1"
                    max="5"
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum of 5 retry attempts allowed
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Retry Timing</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Days
                      </label>
                      <input
                        type="number"
                        value={retryStep.retryTiming.days}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            retrySteps: prev.retrySteps.map((step, idx) => 
                              idx === stepIndex 
                                ? {
                                    ...step,
                                    retryTiming: {
                                      ...step.retryTiming,
                                      days: Math.max(0, parseInt(e.target.value) || 0)
                                    }
                                  }
                                : step
                            )
                          }));
                        }}
                        min="0"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hours
                      </label>
                      <input
                        type="number"
                        value={retryStep.retryTiming.hours}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            retrySteps: prev.retrySteps.map((step, idx) => 
                              idx === stepIndex 
                                ? {
                                    ...step,
                                    retryTiming: {
                                      ...step.retryTiming,
                                      hours: Math.max(0, parseInt(e.target.value) || 0)
                                    }
                                  }
                                : step
                            )
                          }));
                        }}
                        min="0"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Allowed Days</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries({
                      monday: 'Monday',
                      tuesday: 'Tuesday',
                      wednesday: 'Wednesday',
                      thursday: 'Thursday',
                      friday: 'Friday',
                      saturday: 'Saturday',
                      sunday: 'Sunday'
                    }).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            retrySteps: prev.retrySteps.map((step, idx) => 
                              idx === stepIndex 
                                ? {
                                    ...step,
                                    allowedDays: {
                                      ...step.allowedDays,
                                      [key]: !step.allowedDays[key]
                                    }
                                  }
                                : step
                            )
                          }));
                        }}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          retryStep.allowedDays[key]
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Time Window</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={retryStep.timeWindow.start}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            retrySteps: prev.retrySteps.map((step, idx) => 
                              idx === stepIndex 
                                ? {
                                    ...step,
                                    timeWindow: {
                                      ...step.timeWindow,
                                      start: e.target.value
                                    }
                                  }
                                : step
                            )
                          }));
                        }}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={retryStep.timeWindow.end}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            retrySteps: prev.retrySteps.map((step, idx) => 
                              idx === stepIndex 
                                ? {
                                    ...step,
                                    timeWindow: {
                                      ...step.timeWindow,
                                      end: e.target.value
                                    }
                                  }
                                : step
                            )
                          }));
                        }}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Timezone</h3>
                  <select
                    value={retryStep.timezone}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        retrySteps: prev.retrySteps.map((step, idx) => 
                          idx === stepIndex 
                            ? {
                                ...step,
                                timezone: e.target.value
                              }
                            : step
                        )
                      }));
                    }}
                    className="w-full p-2 border rounded bg-white"
                  >
                    <option value="America/Los_Angeles">America/Los_Angeles (UTC-08:00)</option>
                    <option value="America/New_York">America/New_York (UTC-05:00)</option>
                    <option value="Europe/London">Europe/London (UTC+00:00)</option>
                    {/* Add more timezone options as needed */}
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Follow Up Configuration</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Configure what happens during the follow-up call
                  </p>
                </div>

                {/* Dialog Flow or Task/Prompt for Follow Up */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Follow Up Dialog Flow (Optional)
                    </label>
                    <select
                      value={retryStep.followUp.dialogFlow_id}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          retrySteps: prev.retrySteps.map((step, idx) => 
                            idx === stepIndex 
                              ? {
                                  ...step,
                                  followUp: {
                                    ...step.followUp,
                                    dialogFlow_id: e.target.value,
                                    prompt: ''
                                  }
                                }
                              : step
                          )
                        }));
                      }}
                      disabled={!!retryStep.followUp.prompt}
                      className={`w-full p-2 border rounded ${retryStep.followUp.prompt ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                    >
                      <option value="">Select a Dialog Flow</option>
                      {dialogFlows.map(flow => (
                        <option key={flow.id} value={flow.id}>
                          {flow.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Follow Up Task/Prompt {!retryStep.followUp.dialogFlow_id && "(Required)"}
                      </label>
                      <div className="flex space-x-2 items-center">
                        {selectedPrompt ? (
                          <>
                            <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1.5 rounded-md">
                              <span className="text-sm text-blue-700">
                                Editing: {selectedPrompt.name}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    retrySteps: prev.retrySteps.map((step, idx) => 
                                      idx === stepIndex 
                                        ? {
                                            ...step,
                                            followUp: {
                                              ...step.followUp,
                                              prompt: selectedPrompt.prompt
                                            }
                                          }
                                        : step
                                    )
                                  }));
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                onClick={clearSelectedPrompt}
                                className="text-sm text-gray-500 hover:text-gray-700"
                              >
                                ×
                              </button>
                            </div>
                          </>
                        ) : (
                          retryStep.followUp.prompt && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingPrompt(null);
                                setShowSavePromptModal(true);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              Save as New Prompt
                            </button>
                          )
                        )}
                        {savedPrompts.length > 0 && (
                          <SearchablePromptDropdown
                            prompts={savedPrompts}
                            onSelect={(promptId) => {
                              handlePromptSelect(promptId);
                              setFormData(prev => ({
                                ...prev,
                                retrySteps: prev.retrySteps.map((step, idx) => 
                                  idx === stepIndex 
                                    ? {
                                        ...step,
                                        followUp: {
                                          ...step.followUp,
                                          dialogFlow_id: ''
                                        }
                                      }
                                    : step
                                )
                              }));
                            }}
                            onEdit={handleEditPrompt}
                            onDelete={handleDeletePrompt}
                            selectedPromptId={selectedPrompt?.id}
                          />
                        )}
                      </div>
                    </div>
                    <textarea
                      value={retryStep.followUp.prompt}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          retrySteps: prev.retrySteps.map((step, idx) => 
                            idx === stepIndex 
                              ? {
                                  ...step,
                                  followUp: {
                                    ...step.followUp,
                                    prompt: e.target.value,
                                    dialogFlow_id: ''
                                  }
                                }
                              : step
                          )
                        }));
                      }}
                      disabled={!!retryStep.followUp.dialogFlow_id}
                      className={`w-full p-2 border rounded ${retryStep.followUp.dialogFlow_id ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                      rows="4"
                      placeholder={retryStep.followUp.dialogFlow_id ? "Task/Prompt disabled when Dialog Flow is selected" : "Enter the task or prompt for the follow-up call"}
                      required={!retryStep.followUp.dialogFlow_id}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 'recipients': {
        const csvColumnKeys = Object.keys(csvData[0] || {});
        return (
          <div className="space-y-6">
            <div className="rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-6 shadow-[var(--app-panel-shadow)]">
              <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-[var(--app-accent)]">
                    Recipients
                  </p>
                  <h3 className="mt-1 text-lg font-semibold tracking-tight text-[var(--app-accent-strong)]">
                    Who to call
                  </h3>
                  <p className="mt-1 max-w-xl text-sm text-[var(--app-muted)]">
                    Edit inline or import a CSV. Every row needs a valid{' '}
                    <span className="font-medium text-[var(--app-accent-strong)]">phone_number</span>.
                  </p>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-[var(--app-border)]">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h4 className="text-sm font-semibold text-[var(--app-accent-strong)]">Recipients list</h4>
                    <span className="text-xs text-[var(--app-muted)]">
                      {csvData.filter((row) => row.phone_number).length} with a number
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const newRow = Object.fromEntries(csvColumnKeys.map((key) => [key, '']));
                        setCsvData([...csvData, newRow]);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface-strong)] px-3 py-1.5 text-xs font-semibold text-[var(--app-accent)] shadow-sm transition hover:bg-[var(--app-accent-soft)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)]"
                    >
                      <HiPlus className="h-3.5 w-3.5" strokeWidth={2.5} />
                      Add row
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const columnName = prompt('Enter new column name:');
                        if (columnName && String(columnName).trim()) {
                          const key = String(columnName).trim();
                          if (csvColumnKeys.includes(key)) {
                            toast.error('A column with that name already exists.');
                            return;
                          }
                          setCsvData(csvData.map((row) => ({ ...row, [key]: '' })));
                        }
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface-strong)] px-3 py-1.5 text-xs font-semibold text-[var(--app-accent)] shadow-sm transition hover:bg-[var(--app-accent-soft)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)]"
                    >
                      <HiPlus className="h-3.5 w-3.5" strokeWidth={2.5} />
                      Add column
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto bg-[var(--app-surface-strong)]">
                  <table className="min-w-full divide-y divide-[var(--app-border)]">
                    <thead className="bg-[var(--app-surface)]">
                      <tr>
                        {csvColumnKeys.map((header) => {
                          const canRemoveColumn =
                            csvColumnKeys.length > 1 &&
                            String(header).toLowerCase() !== 'phone_number';
                          return (
                            <th
                              key={header}
                              scope="col"
                              className="px-3 py-3 text-left align-bottom"
                            >
                              <div className="flex min-w-[8.5rem] items-end justify-between gap-2">
                                <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--app-muted)]">
                                  {header.replace(/_/g, ' ')}
                                </span>
                                <div className="flex h-8 w-8 shrink-0 items-center justify-end">
                                  {canRemoveColumn ? (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveCsvColumn(header)}
                                      className="rounded-md p-1.5 text-[var(--app-muted)] transition hover:bg-[var(--app-accent-soft)] hover:text-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)]"
                                      title={`Remove column ${header}`}
                                      aria-label={`Remove column ${header}`}
                                    >
                                      <HiTrash className="h-3.5 w-3.5" />
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </th>
                          );
                        })}
                        <th scope="col" className="px-3 py-3 text-right align-bottom">
                          <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--app-muted)]">
                            Row
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--app-border)]">
                      {csvData.map((row, rowIndex) => (
                        <tr key={rowIndex} className="transition-colors hover:bg-[var(--app-surface)]">
                          {csvColumnKeys.map((key) => (
                            <td key={key} className="px-3 py-2 align-middle">
                              <input
                                type="text"
                                value={row[key] ?? ''}
                                onChange={(e) => {
                                  const newData = [...csvData];
                                  newData[rowIndex][key] = e.target.value;
                                  setCsvData(newData);
                                }}
                                placeholder={key === 'phone_number' ? '+1…' : `Enter ${key.replace(/_/g, ' ')}`}
                                autoComplete="off"
                                spellCheck={false}
                                className="w-full min-w-[7rem] rounded-lg border border-[var(--app-field-border)] bg-[var(--app-field-bg)] px-2.5 py-2 text-sm text-[var(--app-ink)] placeholder:text-[var(--app-muted)] focus:border-[var(--app-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--app-accent-ring)]"
                              />
                            </td>
                          ))}
                          <td className="px-3 py-2 text-right align-middle">
                            <button
                              type="button"
                              onClick={() => {
                                const newData = csvData.filter((_, index) => index !== rowIndex);
                                setCsvData(
                                  newData.length
                                    ? newData
                                    : [Object.fromEntries(csvColumnKeys.map((k) => [k, '']))]
                                );
                              }}
                              className="inline-flex items-center gap-1 rounded-lg border border-transparent px-2 py-1 text-xs font-semibold text-red-500 transition hover:bg-red-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50"
                            >
                              <HiTrash className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6">
                <div className="relative" aria-hidden="true">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[var(--app-border)]" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-[var(--app-surface-strong)] px-3 text-xs font-medium uppercase tracking-wider text-[var(--app-muted)]">
                      Or import CSV
                    </span>
                  </div>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-4 flex w-full flex-col items-center rounded-2xl border-2 border-dashed border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-6 text-center transition hover:border-[var(--app-accent)] hover:bg-[var(--app-accent-soft)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)]"
                >
                  <HiUpload className="mb-2 h-8 w-8 text-[var(--app-muted)]" />
                  <p className="text-sm font-medium text-[var(--app-accent-strong)]">
                    Upload a .csv file
                  </p>
                  <p className="mt-1 text-xs text-[var(--app-muted)]">Must include a phone_number column</p>
                </button>

                <div className="mt-4 flex flex-wrap justify-center gap-3 text-center">
                  <button
                    type="button"
                    onClick={handleDownloadStarterCSV}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--app-accent)] underline-offset-4 hover:underline"
                  >
                    <HiDownload className="h-3.5 w-3.5" />
                    Simple template
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadComplexCSV}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--app-accent)] underline-offset-4 hover:underline"
                  >
                    <HiDownload className="h-3.5 w-3.5" />
                    Template with extra columns
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'configuration':
        return (
          <div className="space-y-6">
            <SavePromptModal
              isOpen={showSavePromptModal}
              onClose={() => {
                setShowSavePromptModal(false);
                setEditingPrompt(null);
              }}
              onSave={handleSavePrompt}
              initialName={editingPrompt?.name || ''}
              initialPrompt={editingPrompt?.prompt || formData.prompt}
              isEditing={!!editingPrompt}
            />
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="space-y-6">
                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Name
                  </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter campaign name"
                  />
                </div>

                {/* Model Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model
                  </label>
                  <select
                    value={formData.model}
                    onChange={e => setFormData(prev => ({ ...prev, model: e.target.value }))}
                    className="w-full p-2 border rounded bg-white"
                  >
                    <option value="base">Base</option>
                    <option value="enhanced">Enhanced</option>
                  </select>
                </div>

                {/* Language Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language
                  </label>
                  <select
                    value={formData.language}
                    onChange={e => setFormData(prev => ({ ...prev, language: e.target.value }))}
                    className="w-full p-2 border rounded bg-white"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="hi-Latn">Hindi (Latin script)</option>
                    <option value="el">Greek</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="nl">Dutch</option>
                    <option value="pl">Polish</option>
                    <option value="pt">Portuguese</option>
                    <option value="ru">Russian</option>
                    <option value="sv">Swedish</option>
                    <option value="tr">Turkish</option>
                  </select>
                </div>

                {/* Dialog Flow or Task/Prompt */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dialog Flow (Optional)
                    </label>
                    <select
                      value={formData.dialogFlow_id}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        dialogFlow_id: e.target.value,
                        prompt: '' // Clear prompt when dialog flow is selected
                      }))}
                      disabled={!!formData.prompt}
                      className={`w-full p-2 border rounded ${formData.prompt ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                    >
                      <option value="">Select a Dialog Flow</option>
                      {dialogFlows.map(flow => (
                        <option key={flow.id} value={flow.id}>
                          {flow.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {dialogFlows.length === 0 ? 'Loading available dialog flows...' : 
                       formData.prompt ? 'Dialog Flow selection is disabled while Task/Prompt is filled' :
                       'Select a Dialog Flow or enter a Task/Prompt below, but not both'}
                    </p>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Task/Prompt {!formData.dialogFlow_id && "(Required)"}
                      </label>
                      <div className="flex space-x-2 items-center">
                        {selectedPrompt ? (
                          <>
                            <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1.5 rounded-md">
                              <span className="text-sm text-blue-700">
                                Editing: {selectedPrompt.name}
                              </span>
                              <button
                                type="button"
                                onClick={handleDirectPromptUpdate}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                onClick={clearSelectedPrompt}
                                className="text-sm text-gray-500 hover:text-gray-700"
                              >
                                ×
                              </button>
                            </div>
                          </>
                        ) : (
                          formData.prompt && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingPrompt(null);
                                setShowSavePromptModal(true);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              Save as New Prompt
                            </button>
                          )
                        )}
                        {savedPrompts.length > 0 && (
                          <SearchablePromptDropdown
                            prompts={savedPrompts}
                            onSelect={handlePromptSelect}
                            onEdit={handleEditPrompt}
                            onDelete={handleDeletePrompt}
                            selectedPromptId={selectedPrompt?.id}
                          />
                        )}
                      </div>
                    </div>
                    <textarea
                      value={formData.prompt}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        prompt: e.target.value,
                        dialogFlow_id: '' // Clear dialog flow when prompt is entered
                      }))}
                      disabled={!!formData.dialogFlow_id}
                      className={`w-full p-2 border rounded ${formData.dialogFlow_id ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                      rows="4"
                      placeholder={formData.dialogFlow_id ? "Task/Prompt disabled when Dialog Flow is selected" : "Enter the task or prompt for the campaign"}
                      required={!formData.dialogFlow_id}
                    />
                  </div>
                </div>

                {/* Voice Settings */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voice
                  </label>
                  <SearchableDropdown
                    options={voices}
                    filterOptions={filterCampaignVoiceOptions}
                    onSelect={(voice) => {
                      setSelectedVoice(voice);
                      setFormData(prev => ({
                        ...prev,
                        voice: voice.id
                      }));
                    }}
                    selectedOption={selectedVoice}
                    placeholder="Select a voice"
                    searchPlaceholder="Search voices…"
                    displayKey="name"
                    className="mb-2"
                    headerContent={
                      <div className="flex flex-wrap gap-2 p-3 border-b border-[var(--app-border)] bg-[var(--app-surface-veil)]">
                        {[
                          { id: 'curated', name: 'Curated' },
                          { id: 'your', name: 'Your Voices' },
                          { id: 'favorites', name: 'Favorites' },
                          { id: 'all', name: 'All' }
                        ].map(category => (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => setVoiceCategory(category.id)}
                            className={`
                              px-3 py-1 text-sm font-medium rounded-full border transition-colors
                              ${voiceCategory === category.id
                                ? 'border-[var(--app-accent)] bg-[var(--app-accent-soft)] text-[var(--app-accent)]'
                                : 'border-[var(--app-border)] bg-[var(--app-control-bg)] text-[var(--app-control-fg)] hover:bg-[var(--app-control-bg-hover)]'}
                            `}
                          >
                            {category.name}
                          </button>
                        ))}
                      </div>
                    }
                    renderOption={(voice) => (
                      <div className="py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-base font-medium text-[var(--app-ink)]">
                              {voice.name}
                            </div>
                            {voice.description && (
                              <div className="text-sm text-[var(--app-muted)]">
                                {voice.description}
                              </div>
                            )}
                          </div>
                          {voice.is_favorite && (
                            <HiHeart className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                        {voice.tags && voice.tags.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {voice.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--app-accent-soft)] text-[var(--app-accent)]"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Temperature Setting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temperature (Optional)
                  </label>
                  <input
                    type="number"
                    value={formData.temperature || ''}
                    onChange={e => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) || null }))}
                    className="w-full p-2 border rounded"
                    step="0.1"
                    min="0"
                    max="2"
                    placeholder="Enter temperature (0-2)"
                  />
                </div>

                {/* Background Track */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Track
                  </label>
                  <select
                    value={formData.background_track}
                    onChange={e => setFormData(prev => ({ ...prev, background_track: e.target.value }))}
                    className="w-full p-2 border rounded bg-white"
                  >
                    <option value="none">None</option>
                    <option value="office">Office Ambience</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="street">Street</option>
                  </select>
                </div>

                {/* Additional Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={formData.record}
                      onChange={e => setFormData(prev => ({ ...prev, record: e.target.checked }))}
                      className="rounded w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 ml-2">Record Calls</span>
                  </label>

                  <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={formData.wait_for_greeting}
                      onChange={e => setFormData(prev => ({ ...prev, wait_for_greeting: e.target.checked }))}
                      className="rounded w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 ml-2">Wait for Greeting</span>
                  </label>

                  <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={formData.noise_cancellation}
                      onChange={e => setFormData(prev => ({ ...prev, noise_cancellation: e.target.checked }))}
                      className="rounded w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 ml-2">Noise Cancellation</span>
                  </label>

                  <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={formData.local_dialing}
                      onChange={e => setFormData(prev => ({ ...prev, local_dialing: e.target.checked }))}
                      className="rounded w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 ml-2">Local Dialing</span>
                  </label>

                  <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={formData.answered_by_enabled}
                      onChange={e => setFormData(prev => ({ ...prev, answered_by_enabled: e.target.checked }))}
                      className="rounded w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 ml-2">Enable Answered By Detection</span>
                  </label>
                </div>

                {/* Voicemail Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voicemail Message (Optional)
                  </label>
                  <textarea
                    value={formData.voicemail_message}
                    onChange={e => setFormData(prev => ({ ...prev, voicemail_message: e.target.value }))}
                    className="w-full p-2 border rounded h-24 resize-y"
                    placeholder="Enter message to leave if voicemail is detected..."
                  />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const allTimelineSteps = [...STEPS, ...retrySteps];
  const currentTimelineIndex = allTimelineSteps.findIndex((s) => s.id === currentStep);

  const stepTimelineStatus = (stepId) => {
    const idx = allTimelineSteps.findIndex((s) => s.id === stepId);
    if (idx === -1 || currentTimelineIndex === -1) return 'upcoming';
    if (idx === currentTimelineIndex) return 'current';
    if (idx < currentTimelineIndex) return 'complete';
    return 'upcoming';
  };

  return (
    <AppPageShell maxWidthClass="max-w-7xl" className="min-h-screen">
      <PageHeader
        eyebrow="Campaigns"
        title="Create campaign"
        description="Step through details, contacts, and scheduling to launch a new outbound batch."
      />
        <div className="grid grid-cols-12 gap-8">
          {/* Left Sidebar — progress rail (aligned with app hero surfaces) */}
          <div className="col-span-3">
            <nav
              className="app-stagger-card overflow-hidden rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] shadow-[var(--app-panel-shadow)]"
              style={{ animationDelay: '80ms' }}
              aria-label="Campaign setup steps"
            >
              <div className="border-b border-[var(--app-border)] px-5 pb-4 pt-5">
                <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-[var(--app-accent)]">
                  Progress
                </p>
                <h2 className="mt-1 text-base font-semibold tracking-tight text-[var(--app-accent-strong)]">
                  Campaign steps
                </h2>
                <p className="mt-1 text-sm leading-snug text-[var(--app-muted)]">
                  Jump between stages anytime — optional retries extend the flow.
                </p>
              </div>

              <div className="px-2 pb-5 pt-5">
                <div className="space-y-0.5">
                  {allTimelineSteps.map((step, stepIndex) => {
                    const status = stepTimelineStatus(step.id);
                    const isCurrent = status === 'current';
                    const isLastTimeline = stepIndex === allTimelineSteps.length - 1;

                    return (
                      <div key={step.id} className="group relative">
                        <button
                          type="button"
                          onClick={() => handleStepChange(step.id)}
                          disabled={isCurrent}
                          aria-current={isCurrent ? 'step' : undefined}
                          className={`flex w-full gap-1 rounded-2xl px-2 py-2 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-surface-strong)] ${
                            isCurrent
                              ? 'cursor-default bg-[var(--app-accent-soft)]'
                              : 'cursor-pointer hover:bg-[var(--app-surface)]'
                          }`}
                        >
                          <div className="relative flex w-10 shrink-0 justify-center pt-0.5">
                            {!isLastTimeline ? (
                              <div
                                className="pointer-events-none absolute left-1/2 top-[1.125rem] z-0 w-px -translate-x-1/2 bg-[var(--app-border)]"
                                style={{ bottom: '-0.375rem' }}
                                aria-hidden
                              />
                            ) : null}
                            <div className="relative z-10">
                              <CampaignStepRailDot status={status} />
                            </div>
                          </div>

                          <div className="min-w-0 flex-1 pt-0.5">
                            <div className="flex items-start justify-between gap-2">
                              <span
                                className={`text-sm font-semibold leading-tight ${
                                  status === 'current'
                                    ? 'text-[var(--app-accent)]'
                                    : status === 'complete'
                                      ? 'text-[var(--app-accent-strong)]'
                                      : 'text-[var(--app-muted)]'
                                }`}
                              >
                                {step.name}
                              </span>
                              {!step.required ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveStep(step.id);
                                  }}
                                  className="shrink-0 rounded-lg p-1 text-[var(--app-muted)] opacity-0 transition-opacity hover:bg-[var(--app-surface)] hover:text-red-500 group-hover:opacity-100"
                                  aria-label={`Remove ${step.name}`}
                                >
                                  <HiX className="h-4 w-4" />
                                </button>
                              ) : null}
                            </div>
                            {step.id.startsWith('retry-config-') ? (
                              <div className="mt-1.5 text-xs leading-relaxed text-[var(--app-muted)]">
                                {(() => {
                                  const stepConfig = formData.retrySteps.find((s) => s.id === step.id);
                                  if (!stepConfig) return '';
                                  const conditions = Object.entries(stepConfig.retryConditions).filter(
                                    ([_, value]) => value
                                  ).length;
                                  return `Retry up to ${stepConfig.maxRetryAttempts} times after ${stepConfig.retryTiming.days * 24 + stepConfig.retryTiming.hours} h · ${conditions} conditions`;
                                })()}
                              </div>
                            ) : null}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-2 border-t border-[var(--app-border)] pt-3">
                  <button
                    type="button"
                    onClick={handleAddStep}
                    className="flex w-full gap-1 rounded-2xl px-2 py-2 text-left text-[var(--app-accent)] transition-colors duration-200 hover:bg-[var(--app-accent-soft)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-surface-strong)]"
                  >
                    <div className="flex w-10 shrink-0 justify-center pt-0.5" aria-hidden>
                      <span className="inline-flex h-7 items-center justify-center">
                        <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-[var(--app-accent)] bg-[var(--app-surface-strong)] shadow-[0_0_0_4px_var(--app-accent-soft)]">
                          <HiPlus className="h-2.5 w-2.5 text-[var(--app-accent)]" strokeWidth={3} />
                        </span>
                      </span>
                    </div>
                    <span className="min-w-0 flex-1 pt-0.5">
                      <span className="text-sm font-semibold leading-snug">Add retry &amp; follow-up</span>
                      <span className="mt-0.5 block text-xs font-normal leading-snug text-[var(--app-muted)]">
                        Optional step — not on the main timeline
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </nav>
          </div>

          {/* Right Content Area */}
          <div className="col-span-9">
            {renderStepContent()}

            <div className="mt-6 flex justify-between">
              <div>
                <button
                  onClick={() => navigate('/app/campaigns')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                >
                  Cancel
                </button>
              </div>
              <div className="flex space-x-3">
                {currentStep !== STEPS[0].id && (
                  <button
                    onClick={() => {
                      const allSteps = [...STEPS, ...retrySteps];
                      const currentIndex = allSteps.findIndex(step => step.id === currentStep);
                      handleStepChange(allSteps[currentIndex - 1].id);
                    }}
                    className="px-6 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Previous
                  </button>
                )}
                {currentStep === 'configuration' && retrySteps.length === 0 ? (
                  <>
                    <button
                      onClick={() => {
                        handleAddStep();
                        // Navigate to the newly added retry step
                        const newStepId = `retry-config-${retrySteps.length + 1}`;
                        handleStepChange(newStepId);
                      }}
                      className="px-6 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Add Retry & Follow Up
                    </button>
                    <button
                      onClick={handleSubmit}
                      className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Create & Start Campaign
                    </button>
                  </>
                ) : (
                  currentStep !== retrySteps[retrySteps.length - 1]?.id && (
                    <button
                      onClick={() => {
                        const allSteps = [...STEPS, ...retrySteps];
                        const currentIndex = allSteps.findIndex(step => step.id === currentStep);
                        handleStepChange(allSteps[currentIndex + 1].id);
                      }}
                      className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Next
                    </button>
                  )
                )}
                {currentStep === retrySteps[retrySteps.length - 1]?.id && (
                  <button
                    onClick={handleSubmit}
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Create & Start Campaign
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
    </AppPageShell>
  );
};

export default CreateCampaignPage; 